import { X, MapPin, Phone, Mail, Globe, AlertTriangle, Clock, Heart, FileEdit, Users, BadgeCheck } from "lucide-react";
import { trackEvent } from "@/hooks/useEventTracking";
import { ShareChoiceDialog } from "./ShareChoiceDialog";
import { Button } from "@/components/ui/button";
import type { Shelter } from "@/types/shelter";
import { isUrgentNeedsActive } from "@/types/shelter";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ShelterDetailProps {
  shelter: Shelter;
  onClose: () => void;
  source?: string;
}

export const ShelterDetail = ({ shelter, onClose, source }: ShelterDetailProps) => {
  const formattedDate = shelter.last_updated
    ? format(new Date(shelter.last_updated), "d MMMM yyyy", { locale: pl })
    : null;


  return (
    <div className="fixed inset-0 z-[1001] flex items-end sm:items-center justify-center">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-card rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] sm:max-h-[85vh] max-h-[calc(100dvh-env(safe-area-inset-top)-2rem)] overflow-auto animate-slide-in shadow-elevated">
        {/* Header */}
        <div className="sticky top-0 bg-card z-10 px-6 py-4 border-b border-border flex items-start justify-between">
          <div className="pr-8">
            <h2 className="font-display font-bold text-xl text-card-foreground">
              {shelter.name}
            </h2>
            <p className="text-muted-foreground mt-1">
              {shelter.city}{shelter.region && `, ${shelter.region}`}
            </p>
            {/* Organization verified badge */}
            {shelter.organization_verified && (
              <div className="mt-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/90 text-primary-foreground">
                  <BadgeCheck className="w-3.5 h-3.5" />
                  Zweryfikowane przez organizację
                </span>
                {shelter.organization_verified_at && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Data weryfikacji: {format(new Date(shelter.organization_verified_at), "d MMMM yyyy", { locale: pl })}
                  </p>
                )}
              </div>
            )}
            {/* Unverified data badge with tooltip */}
            {!shelter.data_verified && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="text-xs text-muted-foreground/60 mt-1 italic cursor-help">
                      Dane niezweryfikowane – pochodzą z internetu
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[280px] text-center">
                    <p>Informacje pochodzą z internetu i nie zostały jeszcze potwierdzone przez społeczność ani schronisko. Pomóż je zweryfikować!</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-muted hover:bg-accent transition-colors"
            aria-label="Zamknij"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">
          {/* Urgent needs badge - only this one at the top */}
          {isUrgentNeedsActive(shelter) && (
            <div className="flex">
              <span className="inline-flex items-center gap-2 badge-urgent text-sm">
                <AlertTriangle className="w-4 h-4" />
                Pilne potrzeby
              </span>
            </div>
          )}

          {/* Description */}
          {shelter.description && (
            <div className="bg-muted/50 rounded-xl p-4">
              <h3 className="font-display font-semibold text-card-foreground mb-2 text-sm">
                O schronisku
              </h3>
              <p className="text-muted-foreground text-sm">{shelter.description}</p>
            </div>
          )}

          {/* Current Needs */}
          {shelter.current_needs && (
            <div className="bg-accent/50 rounded-xl p-4">
              <h3 className="font-display font-semibold text-accent-foreground mb-2 flex items-center gap-2 text-sm">
                <AlertTriangle className="w-4 h-4" />
                Obecne potrzeby
              </h3>
              <p className="text-card-foreground text-sm whitespace-pre-line">{shelter.current_needs}</p>
            </div>
          )}

          {/* Volunteer Info */}
          <div className="bg-accent/50 rounded-xl p-4">
            <h3 className="font-display font-semibold text-accent-foreground mb-3 flex items-center gap-2 text-sm">
              <Users className="w-4 h-4" />
              Wolontariat i spacery
            </h3>
            
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${shelter.dog_walking_available === true ? 'bg-walking' : shelter.dog_walking_available === false ? 'bg-destructive/50' : 'bg-muted-foreground/30'}`} />
                <span className="text-accent-foreground">
                  Spacery z psami: {shelter.dog_walking_available === true ? 'możliwe' : shelter.dog_walking_available === false ? 'niedostępne' : 'brak informacji'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${shelter.volunteering_available === true ? 'bg-teal-600' : shelter.volunteering_available === false ? 'bg-destructive/50' : 'bg-muted-foreground/30'}`} />
                <span className="text-accent-foreground">
                  Wolontariat: {shelter.volunteering_available === true ? 'możliwy' : shelter.volunteering_available === false ? 'niedostępny' : 'brak informacji'}
                </span>
              </div>
            </div>
            
            {shelter.volunteer_info && (
              <p className="text-accent-foreground/80 text-xs mt-3 pt-3 border-t border-primary/15">{shelter.volunteer_info}</p>
            )}
          </div>

          {/* Adoption Info */}
          <div className="bg-accent/50 rounded-xl p-4">
            <h3 className="font-display font-semibold text-accent-foreground mb-3 flex items-center gap-2 text-sm">
              <Heart className="w-4 h-4" />
              Adopcja
            </h3>
            
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${shelter.dogs_available === true ? 'bg-blue-300' : shelter.dogs_available === false ? 'bg-destructive/50' : 'bg-muted-foreground/30'}`} />
                <span className="text-accent-foreground">
                  Psy do adopcji: {shelter.dogs_available === true ? 'dostępne' : shelter.dogs_available === false ? 'niedostępne' : 'brak informacji'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${shelter.cats_available === true ? 'bg-purple-300' : shelter.cats_available === false ? 'bg-destructive/50' : 'bg-muted-foreground/30'}`} />
                <span className="text-accent-foreground">
                  Koty do adopcji: {shelter.cats_available === true ? 'dostępne' : shelter.cats_available === false ? 'niedostępne' : 'brak informacji'}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-2.5">
            <h3 className="font-display font-semibold text-card-foreground text-sm">
              Dane kontaktowe
            </h3>
            
            {(shelter.address || shelter.postal_code) && (
              <a 
                href={`https://maps.google.com/?q=${encodeURIComponent((shelter.address ? shelter.address + ', ' : '') + (shelter.postal_code ? shelter.postal_code + ' ' : '') + shelter.city)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("Adres - mapa", { shelter_id: shelter.id, shelter_name: shelter.name })}
                className="flex items-start gap-2.5 text-muted-foreground hover:text-primary transition-colors text-sm"
              >
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  {shelter.address && `${shelter.address}, `}
                  {shelter.postal_code && `${shelter.postal_code} `}
                  {shelter.city}
                </span>
              </a>
            )}
            
            {shelter.phone && (
              <a 
                href={`tel:${shelter.phone}`}
                onClick={() => trackEvent("Telefon", { shelter_id: shelter.id, shelter_name: shelter.name })}
                className="flex items-center gap-2.5 text-muted-foreground hover:text-primary transition-colors text-xs"
              >
                <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{shelter.phone}</span>
              </a>
            )}
            
            {shelter.email && (
              <a 
                href={`mailto:${shelter.email}`}
                onClick={() => trackEvent("Email", { shelter_id: shelter.id, shelter_name: shelter.name })}
                className="flex items-center gap-2.5 text-muted-foreground hover:text-primary transition-colors text-sm"
              >
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span>{shelter.email}</span>
              </a>
            )}
            
            {shelter.website && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a 
                      href={shelter.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent("Strona www", { shelter_id: shelter.id, shelter_name: shelter.name })}
                      className="flex items-center gap-2.5 text-muted-foreground hover:text-primary transition-colors text-sm"
                    >
                      <Globe className="w-4 h-4 flex-shrink-0" />
                      <span className="break-all">
                        {(() => {
                          const url = shelter.website.replace(/^https?:\/\//, '');
                          return url.length > 30 ? url.substring(0, 30) + '...' : url;
                        })()}
                      </span>
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>{shelter.website}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {!shelter.address && !shelter.postal_code && !shelter.phone && !shelter.email && !shelter.website && (
              <p className="text-muted-foreground text-xs">Brak danych kontaktowych</p>
            )}
          </div>

          {/* Last Updated */}
          {formattedDate && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2 border-t border-border">
              <Clock className="w-4 h-4" />
              <span>Ostatnia aktualizacja: {formattedDate}</span>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-card px-6 py-3 border-t border-border">
          <div className="flex flex-col gap-2">
            {/* Share Button - dark green primary */}
            <ShareChoiceDialog shelter={shelter} variant="button" />
            
            {/* Update Buttons Row */}
            <div className="flex flex-col sm:flex-row gap-2">
              <a
                href={`/zglos?shelter=${shelter.id}&type=needs`}
                onClick={() => trackEvent("Zaktualizuj potrzeby", { shelter_id: shelter.id, source: source || "unknown" })}
                className="flex items-center justify-center gap-1.5 flex-1 px-3 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-semibold hover:bg-accent/80 transition-colors"
              >
                <Heart className="w-4 h-4" />
                Zaktualizuj potrzeby
              </a>
              <a
                href={`/zglos?shelter=${shelter.id}&type=info`}
                onClick={() => trackEvent("Zaktualizuj dane", { shelter_id: shelter.id, source: source || "unknown" })}
                className="flex items-center justify-center gap-1.5 flex-1 px-3 py-2 bg-card text-foreground border border-border rounded-lg text-sm font-semibold hover:bg-muted transition-colors"
              >
                <FileEdit className="w-4 h-4" />
                Zaktualizuj dane
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
